import { useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Image } from 'react-konva';
import { Annotation } from './Annotation';
import { Annotation as AnnotationType, Dimension } from '../annotationTypes';

// import "./ImageCanvasPreview.scss";
interface ImageCanvasProps {
  imageUrl: string;
  data: any;
  damages: any;
  pageInfo: number;
  dimensions: { width: number; height: number };
  onModalToggle: (item: any) => void;
  user: any;
}

function ImageCanvasPreview({ imageUrl, data, damages, pageInfo, onModalToggle, dimensions, user }: ImageCanvasProps) {
  const [images, setImages] = useState<HTMLImageElement | null>(null);
  const [imageMeasures, setImageMeasures] = useState<Dimension>({
    width: 0,
    height: 0,
  });
  const imageRef = useRef(null);
  const autoOpenKeyRef = useRef<string | null>(null);
  // Calculate scale factor for preview
  const removeAnnotation = () => {};
  const clickAnnotation = (id: string) => {};
  useEffect(() => {
    const imageToLoad: HTMLImageElement = new window.Image();
    const handleLoad = () => {
      setImageMeasures({
        width: imageToLoad.width,
        height: imageToLoad.height,
      });
      setImages(imageToLoad);
    };

    // Display via the media URL directly (signed URL on cloud; open URL on on-prem).
    imageToLoad.src = imageUrl;
    imageToLoad.addEventListener('load', handleLoad);

    return () => {
      imageToLoad.removeEventListener('load', handleLoad);
    };
  }, [imageUrl]);

  const annotations = useMemo(() => {
    // Prevent division by zero if image dimensions are not yet loaded
    if (imageMeasures.width === 0 || imageMeasures.height === 0) {
      return [];
    }

    let scaleX = dimensions.width / imageMeasures.width;
    let scaleY = dimensions.height / imageMeasures.height;
    return (damages
      ?.map((res: any) => {
        const annotation: AnnotationType | undefined = res?.newMetadata ?? res?.metadata;

        if (!annotation || !annotation.x) return null;

        const { x, y, x1, y1, x2, y2, width, height, type, imageWidth, imageHeight } = annotation;

        if (!type) return null;

        if (imageWidth && imageHeight) {
          scaleX = dimensions.width / imageWidth;
          scaleY = dimensions.height / imageHeight;
        }

        let label = type;
        if (type.length < 7) label = `${type}-${res?.source?.[0]?.toUpperCase() || ''}`;

        return {
          rotation: annotation.rotation ?? 0,
          x: (x < 0.2 ? 0.1 : x) * scaleX,
          y: (y < 0.2 ? 0.1 : y) * scaleY,
          x1: (x1 < 0.2 ? 0.1 : x1) * scaleX,
          y1: (y1 < 0.2 ? 0.1 : y1) * scaleY,
          x2: x2 * scaleX,
          y2: y2 * scaleY,
          width: width * scaleX,
          height: height * scaleY,
          type: label,
          id: res.id,
          review_status: 'APPROVED',
          deleted: 0,
        };
      })
      .filter(Boolean) ?? []) as AnnotationType[];
  }, [damages, dimensions, imageMeasures]);

  useEffect(() => {
    const autoOpenKey = data?.id ?? data?.imageFileId ?? imageUrl;

    if (data?.isClickable && imageRef.current && autoOpenKeyRef.current !== autoOpenKey) {
      autoOpenKeyRef.current = autoOpenKey;
      const timerId = window.setTimeout(() => {
        onModalToggle(data);
      }, 2000);

      return () => {
        window.clearTimeout(timerId);
      };
    }
  }, [data, imageUrl, onModalToggle]);

  return (
    <div className="ImageCanvasPreview cursor-pointer" onClick={() => onModalToggle(data)}>
      {imageUrl && (
        // @ts-ignore
        <Stage
          width={dimensions.width}
          height={dimensions.height}
          pixelRatio={Math.min(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 3)}>
          <Layer>
            <Image
              ref={imageRef}
              className="canvas-img"
              image={images || undefined}
              imageSmoothingEnabled
              width={dimensions.width}
              height={dimensions.height}
              onClick={() => onModalToggle(data)}
            />
            {annotations?.map((annotation: AnnotationType, i) => {
              if (annotation.deleted !== 0) {
                return null;
              }

              return (
                <Annotation
                  key={annotation.id || i}
                  shapeProps={annotation}
                  isSelected={false}
                  isEditable={false}
                  hoverCursor="pointer"
                  user={user}
                  removeAnnotation={removeAnnotation}
                  onSelect={() => {
                    clickAnnotation(annotation.id);
                  }}
                  onChange={() => {}}
                />
              );
            })}
          </Layer>
        </Stage>
      )}
    </div>
  );
}

export default ImageCanvasPreview;
